<template>
  <div class="onboarding">
    <div class="onboarding-container">
      <!-- Progress bar -->
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: `${(step / totalSteps) * 100}%` }"></div>
      </div>
      <div class="step-indicator">Step {{ step }} of {{ totalSteps }}</div>

      <!-- Step 1: Welcome -->
      <div v-if="step === 1" class="step-content">
        <div class="welcome-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        </div>
        <h1>Welcome to Budget Buddy</h1>
        <p class="step-description">
          Budget Buddy helps you track retail purchases by matching receipts to your Monarch Money transactions.
          Let's get you set up in just a few steps.
        </p>
        <div class="action-buttons">
          <button @click="skipOnboarding" class="btn-text">Skip setup</button>
          <button @click="nextStep" class="btn-primary">Get Started</button>
        </div>
      </div>

      <!-- Step 2: Connect Monarch -->
      <div v-if="step === 2" class="step-content">
        <div class="step-header">
          <span class="step-number">1</span>
          <h2>Connect Monarch Money</h2>
        </div>
        <p class="step-description">
          Connect your Monarch Money account to sync your transactions and categories.
          This allows Budget Buddy to match your retailer receipts with your actual spending.
        </p>

        <div class="monarch-section">
          <MonarchConnect />
          <p v-if="monarchConnected" class="success-message">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Monarch connected successfully!
          </p>
        </div>

        <div class="action-buttons">
          <button @click="prevStep" class="btn-text">&larr; Back</button>
          <div class="right-buttons">
            <button @click="nextStep" class="btn-text">Skip for now</button>
            <button @click="nextStep" class="btn-primary" :disabled="!monarchConnected">Continue</button>
          </div>
        </div>
      </div>

      <!-- Step 3: Email Forwarding -->
      <div v-if="step === 3" class="step-content">
        <div class="step-header">
          <span class="step-number">2</span>
          <h2>Set Up Email Forwarding</h2>
        </div>
        <p class="step-description">
          Forward your retailer receipt emails to Budget Buddy for automatic processing.
          This is how we capture your itemized purchases.
        </p>

        <div class="forwarding-preview">
          <div class="forwarding-status" v-if="forwardingVerified">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>Email forwarding is already configured!</span>
          </div>
          <div v-else class="setup-prompt">
            <p>You'll need to:</p>
            <ol>
              <li>Add our email as a forwarding address in Gmail</li>
              <li>Create a filter to auto-forward retailer receipts</li>
            </ol>
            <router-link to="/forwarding/setup" class="btn-secondary">
              Set Up Forwarding
            </router-link>
          </div>
        </div>

        <div class="action-buttons">
          <button @click="prevStep" class="btn-text">&larr; Back</button>
          <div class="right-buttons">
            <button @click="nextStep" class="btn-text">Skip for now</button>
            <button @click="nextStep" class="btn-primary">Continue</button>
          </div>
        </div>
      </div>

      <!-- Step 4: Tutorial -->
      <div v-if="step === 4" class="step-content tutorial-step">
        <h2>How to Use Budget Buddy</h2>
        <p class="step-description">Here's a quick overview of the key features:</p>

        <div class="feature-cards">
          <div class="feature-card">
            <div class="feature-icon feature-icon-sync">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </div>
            <h3>Auto-Sync</h3>
            <p>Receipts sync automatically when forwarded emails arrive. You can also manually refresh from the dashboard.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon feature-icon-review">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <h3>Review Receipts</h3>
            <p>Click the green "Ready to Review" card on the dashboard to process receipts one by one and match them to transactions.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon feature-icon-settings">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </div>
            <h3>Settings</h3>
            <p>Adjust which spending categories are tracked. Only transactions in selected categories will be matched with receipts.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon feature-icon-edit">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </div>
            <h3>Edit Transactions</h3>
            <p>You can always go back to any transaction to re-categorize items or adjust how purchases are split.</p>
          </div>
        </div>

        <div class="action-buttons">
          <button @click="prevStep" class="btn-text">&larr; Back</button>
          <button @click="nextStep" class="btn-primary">Got it!</button>
        </div>
      </div>

      <!-- Step 5: Complete -->
      <div v-if="step === 5" class="step-content complete-step">
        <div class="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h1>You're All Set!</h1>
        <p class="step-description">
          You're ready to start using Budget Buddy. Head to the dashboard to see your receipts and transactions.
        </p>
        <div class="action-buttons centered">
          <button @click="completeOnboarding" class="btn-primary btn-large">Go to Dashboard</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { syncAPI, forwardingAPI } from '../services/api'
import MonarchConnect from '../components/MonarchConnect.vue'

const router = useRouter()

const step = ref(1)
const totalSteps = 5
const monarchConnected = ref(false)
const forwardingVerified = ref(false)
let statusPollInterval = null

onMounted(async () => {
  // Check current setup status
  await checkSetupStatus()
})

onUnmounted(() => {
  if (statusPollInterval) {
    clearInterval(statusPollInterval)
  }
})

// Poll for Monarch status when on step 2
watch(step, (newStep) => {
  if (newStep === 2 && !monarchConnected.value) {
    // Start polling for Monarch connection status
    statusPollInterval = setInterval(async () => {
      try {
        const monarchRes = await syncAPI.getMonarchStatus()
        if (monarchRes.data.connected) {
          monarchConnected.value = true
          clearInterval(statusPollInterval)
        }
      } catch (err) {
        console.error('Error polling Monarch status:', err)
      }
    }, 2000)
  } else if (statusPollInterval) {
    clearInterval(statusPollInterval)
  }
})

const checkSetupStatus = async () => {
  try {
    // Check Monarch connection
    const monarchRes = await syncAPI.getMonarchStatus()
    monarchConnected.value = monarchRes.data.connected

    // Check forwarding status
    const forwardingRes = await forwardingAPI.getStatus()
    forwardingVerified.value = forwardingRes.data.forwarding_verified

    // If both are already set up, mark onboarding complete and redirect
    if (monarchConnected.value && forwardingVerified.value) {
      localStorage.setItem('onboardingComplete', 'true')
      router.push('/dashboard')
    }
  } catch (err) {
    console.error('Error checking setup status:', err)
  }
}

const nextStep = () => {
  if (step.value < totalSteps) {
    step.value++
  }
}

const prevStep = () => {
  if (step.value > 1) {
    step.value--
  }
}

const skipOnboarding = () => {
  localStorage.setItem('onboardingComplete', 'true')
  localStorage.setItem('onboardingSkipped', 'true')
  router.push('/dashboard')
}

const completeOnboarding = () => {
  localStorage.setItem('onboardingComplete', 'true')
  router.push('/dashboard')
}
</script>

<style scoped>
.onboarding {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.onboarding-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 600px;
  padding: 2rem;
}

.progress-bar {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transition: width 0.3s ease;
}

.step-indicator {
  text-align: right;
  font-size: 0.85rem;
  color: #7f8c8d;
  margin-bottom: 1.5rem;
}

.step-content {
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.welcome-icon {
  text-align: center;
  color: #667eea;
  margin-bottom: 1rem;
}

.step-content h1 {
  text-align: center;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.step-content h2 {
  color: #2c3e50;
  margin: 0;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #667eea;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  flex-shrink: 0;
}

.step-description {
  color: #7f8c8d;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  text-align: center;
}

.step-header + .step-description {
  text-align: left;
}

.monarch-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
}

.success-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #27ae60;
  margin-top: 1rem;
  font-weight: 500;
}

.forwarding-preview {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.forwarding-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #27ae60;
  font-weight: 500;
}

.setup-prompt p {
  margin: 0 0 0.75rem;
  color: #2c3e50;
}

.setup-prompt ol {
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #7f8c8d;
}

.setup-prompt li {
  margin-bottom: 0.25rem;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.action-buttons.centered {
  justify-content: center;
}

.right-buttons {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1.1rem;
}

.btn-secondary {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #2980b9;
}

.btn-text {
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  font-size: 0.95rem;
  padding: 0.5rem;
}

.btn-text:hover {
  color: #2c3e50;
}

/* Tutorial step */
.tutorial-step h2 {
  text-align: center;
  margin-bottom: 0.5rem;
}

.feature-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.feature-card {
  background: #f8f9fa;
  padding: 1.25rem;
  border-radius: 8px;
  text-align: center;
}

.feature-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem;
}

.feature-icon-sync {
  background: #e8f4fc;
  color: #3498db;
}

.feature-icon-review {
  background: #e8f6ef;
  color: #27ae60;
}

.feature-icon-settings {
  background: #fef5e7;
  color: #e67e22;
}

.feature-icon-edit {
  background: #fdf2f8;
  color: #9b59b6;
}

.feature-card h3 {
  color: #2c3e50;
  font-size: 1rem;
  margin: 0 0 0.5rem;
}

.feature-card p {
  color: #7f8c8d;
  font-size: 0.85rem;
  line-height: 1.4;
  margin: 0;
}

/* Complete step */
.complete-step {
  text-align: center;
}

.success-icon {
  color: #27ae60;
  margin-bottom: 1rem;
}

.complete-step h1 {
  color: #27ae60;
}

@media (max-width: 500px) {
  .feature-cards {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    flex-direction: column;
    gap: 1rem;
  }

  .right-buttons {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
